import { resolveAuthUserDocument } from './authUserIdentity';
import { db } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  setDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { LOCALE_STORAGE_KEY } from '../i18n/i18n';

interface FirestoreUser {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  personId?: number | string | null;
  yearOfBirth?: number | null;
  dateOfBirth?: Date | null;
  nfifLicenseNumber?: string | null;
  gender?: 'male' | 'female' | '';
  nationality?: string | null;
  representing?: string[] | string | null;
  email?: string;
  phoneCountryCode?: string | null;
  phone?: string | null;
  uid?: string | null;
  userId?: string | null;
  sendRunningOffers?: boolean;
  notifyFutureEvents?: boolean;
  languagePreference?: string | null;
}

interface FirestorePublicProfile {
  personId?: number | string | null;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  nationality?: string | null;
  representing?: string[] | string | null;
  yearOfBirth?: number | null;
  dateOfBirth?: Date | null;
  gender?: 'male' | 'female' | '';
}

export interface RunnerParticipation {
  editionId: string;
  year: number;
  eventDate?: Date | null;
  raceName: string;
  distanceKey?: string | null;
  raceRank?: number | null;
  raceTimeSeconds?: number | null;
  raceTimeDisplay?: string | null;
  totalRank?: number | null;
  totalTimeSeconds?: number | null;
  totalTimeDisplay?: string | null;
  loopsCompleted: number;
  status?: string | null;
  hasCheckpointData: boolean;
}

export interface RunnerBestPerformance {
  editionId: string;
  year: number;
  loops: number;
  totalTimeSeconds: number | null;
  totalTimeDisplay?: string | null;
  raceName: string;
}

export interface RunnerKutcParticipationData {
  participations: RunnerParticipation[];
  totalAppearances: number;
  appearanceYears: number[];
  totalLoops: number;
  bestPerformance: RunnerBestPerformance | null;
}

export interface RunnerProfile {
  routeId: string;
  userId: string;
  userDocId: string;
  personId: number | null;
  firstName: string;
  lastName: string;
  email: string;
  phoneCountryCode?: string | null;
  phone?: string | null;
  displayName?: string;
  dateOfBirth?: Date | null;
  yearOfBirth?: number | null;
  nfifLicenseNumber?: string | null;
  gender?: 'male' | 'female' | '';
  nationality?: string | null;
  representing?: string[];
  sendRunningOffers?: boolean;
  notifyFutureEvents?: boolean;
  languagePreference?: string | null;
  totalAppearances: number;
  appearanceYears: number[];
  totalLoops: number;
  bestPerformance: RunnerBestPerformance | null;
  participations: RunnerParticipation[];
  upcomingRegistrations: RunnerUpcomingRegistration[];
}

export interface GetRunnerProfileOptions {
  identityMode?: 'auth-account' | 'user-document';
  includeParticipations?: boolean;
  includeUpcomingRegistrations?: boolean;
  includePrivateDetails?: boolean;
}

export interface RunnerProfileEditableDetails {
  firstName: string;
  lastName: string;
  displayName?: string;
  phoneCountryCode?: string;
  phone?: string;
  dateOfBirth?: Date | null;
  yearOfBirth?: number | null;
  nfifLicenseNumber?: string | null;
  gender?: 'male' | 'female' | '';
  nationality?: string | null;
  representing?: string[];
  sendRunningOffers?: boolean;
  notifyFutureEvents?: boolean;
  languagePreference?: string | null;
}

export interface LoadedRunnerProfileIdentity {
  userDocId: string;
  personId: number | null;
}

export interface RunnerProfileUpdateResult {
  status: 'saved';
  publicPublication: 'published' | 'held' | 'unassigned';
}

export interface RunnerUpcomingRegistration {
  registrationId: string;
  editionId: string;
  eventName: string;
  eventShortName?: string;
  startTime: Date | null;
  cancellationDeadline?: Date | null;
  eventStatus?: string | null;
  registrationType: 'kutc' | 'mo' | 'event';
  status?: string | null;
  registrationNumber?: number | null;
  raceDistance?: string | null;
  raceDistanceLabel?: string | null;
  updatedAt?: Date | null;
  paymentRequired?: number;
  paymentMade?: number;
  paymentStatus?: 'pending' | 'completed' | 'refunded';
  payments?: Array<{
    date: Date;
    method: string;
    amount: number;
    comment: string;
    paymentMethodType?: string;
    paymentMethodDetails?: any;
    chargeId?: string | null;
    receiptUrl?: string | null;
  }>;
}

const deriveDistanceKey = (race: any, index: number): string => {
  if (race?.distanceKey) return String(race.distanceKey);
  if (race?.key) return String(race.key);
  if (race?.raceKey) return String(race.raceKey);
  if (race?.raceName) {
    return String(race.raceName).toLowerCase().replace(/\s+/g, '-');
  }
  return `race-${index}`;
};

const toDateSafe = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseYearFromEditionId = (editionId: string): number => {
  const parts = editionId.split('-');
  const yearStr = parts[parts.length - 1];
  const year = Number(yearStr);
  return Number.isFinite(year) ? year : 0;
};

const ensureNumber = (value: any): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const ensureLoops = (value: any): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeId = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toDateFromUnknown = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const upcomingStatusFilter = ['pending', 'confirmed'];

export async function getPublicUpcomingRegistrations(personId: number): Promise<RunnerUpcomingRegistration[]> {
  if (!Number.isFinite(personId)) {
    return [];
  }

  const now = Timestamp.now();
  const editionsRef = collection(db, 'eventEditions');
  const editionsQuery = query(editionsRef, where('startTime', '>', now), orderBy('startTime', 'asc'));
  const editionsSnapshot = await getDocs(editionsQuery);
  if (editionsSnapshot.empty) {
    return [];
  }

  const editions = editionsSnapshot.docs.map((editionDoc) => {
    const editionData = editionDoc.data() as any;
    const editionId = editionDoc.id;
    const startTime = toDateFromUnknown(editionData.startTime);
    const cancellationDeadline = toDateFromUnknown(editionData.cancellationDeadline);
    const eventName = editionData.eventName || editionId;
    const eventShortName = editionData.eventShortName || editionData.eventId || undefined;
    const raceDistanceDefs: any[] = Array.isArray(editionData.raceDistances) ? editionData.raceDistances : [];
    const type: RunnerUpcomingRegistration['registrationType'] = editionId.toLowerCase().startsWith('kutc-')
      ? 'kutc'
      : editionId.toLowerCase().startsWith('mo-')
        ? 'mo'
        : 'event';

    return {
      editionId,
      startTime,
      cancellationDeadline,
      eventName,
      eventShortName,
      eventStatus: editionData.status || null,
      raceDistanceDefs,
      type
    };
  });

  const editionIdSet = new Set(editions.map((e) => e.editionId));

  const results: RunnerUpcomingRegistration[] = [];

  const regSnapshot = await getDocs(
    query(collection(db, 'publicRegistrations'), where('personId', '==', personId))
  );
  regSnapshot.forEach((docSnap) => {
    const data = docSnap.data() as any;
    const editionId = String(data.editionId || '').trim();
    if (!editionIdSet.has(editionId)) {
      return;
    }
    const status = String(data.status || '').toLowerCase();
    if (!upcomingStatusFilter.includes(status)) {
      return;
    }
    const edition = editions.find((e) => e.editionId === editionId);
    if (!edition) {
      return;
    }

    const raceDistance = data.raceDistance ? String(data.raceDistance) : null;
    const rd = raceDistance
      ? edition.raceDistanceDefs.find((it) => String(it?.id || it?.key || '').toLowerCase() === raceDistance.toLowerCase())
      : null;
    const raceDistanceLabel = rd
      ? (rd.displayName || rd.displayName_en || rd.displayName_no || rd.name || null)
      : null;

    results.push({
      registrationId: docSnap.id,
      editionId,
      eventName: edition.eventName,
      eventShortName: edition.eventShortName,
      startTime: edition.startTime,
      cancellationDeadline: edition.cancellationDeadline,
      eventStatus: edition.eventStatus,
      registrationType: edition.type,
      status: data.status || null,
      registrationNumber: typeof data.registrationNumber === 'number' ? data.registrationNumber : null,
      raceDistance,
      raceDistanceLabel,
      updatedAt: toDateFromUnknown(data.updatedAt),
      paymentRequired: typeof data.paymentRequired === 'number' ? data.paymentRequired : 0,
      paymentMade: typeof data.paymentMade === 'number' ? data.paymentMade : 0,
      paymentStatus: data.paymentStatus || 'pending',
      payments: Array.isArray(data.payments) ? data.payments.map((p: any) => ({
        date: toDateFromUnknown(p.date) || new Date(),
        method: p.method || 'stripe',
        amount: typeof p.amount === 'number' ? p.amount : 0,
        comment: p.comment || '',
        paymentMethodType: p.paymentMethodType || 'unknown',
        paymentMethodDetails: p.paymentMethodDetails || null,
        chargeId: p.chargeId || null,
        receiptUrl: p.receiptUrl || null
      })) : []
    });
  });

  const seen = new Set<string>();
  const unique = results.filter((it) => {
    const key = `${it.editionId}::${it.registrationType}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  unique.sort((a, b) => {
    const aTime = a.startTime?.getTime?.() ?? 0;
    const bTime = b.startTime?.getTime?.() ?? 0;
    return aTime - bTime;
  });

  return unique;
}

async function fetchUpcomingRegistrations(userId: string, personId: number | null): Promise<RunnerUpcomingRegistration[]> {
  if (!userId) {
    return [];
  }

  const now = Timestamp.now();
  const editionsRef = collection(db, 'eventEditions');
  const editionsQuery = query(editionsRef, where('startTime', '>', now), orderBy('startTime', 'asc'));
  const editionsSnapshot = await getDocs(editionsQuery);

  if (editionsSnapshot.empty) {
    return [];
  }

  const results: RunnerUpcomingRegistration[] = [];

  await Promise.all(
    editionsSnapshot.docs.map(async (editionDoc) => {
      const editionData = editionDoc.data() as any;
      const editionId = editionDoc.id;
      const startTime = toDateFromUnknown(editionData.startTime);
      const cancellationDeadline = toDateFromUnknown(editionData.cancellationDeadline);
      const eventName = editionData.eventName || editionId;
      const eventShortName = editionData.eventShortName || editionData.eventId || undefined;
      const eventStatus = editionData.status || null;
      const raceDistanceDefs: any[] = Array.isArray(editionData.raceDistances) ? editionData.raceDistances : [];
      const type: RunnerUpcomingRegistration['registrationType'] = editionId.toLowerCase().startsWith('kutc-')
        ? 'kutc'
        : editionId.toLowerCase().startsWith('mo-')
          ? 'mo'
          : 'event';

      if (type === 'kutc' || type === 'event' || type === 'mo') {
        const registrationsRef = collection(db, 'registrations');
        const regQuery = query(
          registrationsRef,
          where('editionId', '==', editionId),
          where('userId', '==', userId),
          where('status', 'in', upcomingStatusFilter)
        );
        const seenRegistrationIds = new Set<string>();

        const pushRegistrationDoc = (docSnap: any) => {
          if (!docSnap || seenRegistrationIds.has(docSnap.id)) {
            return;
          }
          seenRegistrationIds.add(docSnap.id);
          const data = docSnap.data() as any;
          if (data.userId !== userId || (data.personId != null && data.personId !== personId))
            throw new Error('Registration identity conflicts with its linked account.');
          const raceDistance = data.raceDistance ? String(data.raceDistance) : null;
          const rd = raceDistance
            ? raceDistanceDefs.find((it) => String(it?.id || it?.key || '').toLowerCase() === raceDistance.toLowerCase())
            : null;
          const raceDistanceLabel = rd
            ? (rd.displayName || rd.displayName_en || rd.displayName_no || rd.name || null)
            : null;
          const updatedAt = toDateFromUnknown(data.updatedAt || data.createdAt);
          results.push({
            registrationId: docSnap.id,
            editionId,
            eventName,
            eventShortName,
            startTime,
            cancellationDeadline,
            eventStatus,
            registrationType: type,
            status: data.status || null,
            registrationNumber: typeof data.registrationNumber === 'number' ? data.registrationNumber : null,
            raceDistance,
            raceDistanceLabel,
            updatedAt,
            paymentRequired: typeof data.paymentRequired === 'number' ? data.paymentRequired : 0,
            paymentMade: typeof data.paymentMade === 'number' ? data.paymentMade : 0,
            paymentStatus: data.paymentStatus || 'pending',
            payments: Array.isArray(data.payments) ? data.payments.map((p: any) => ({
              date: toDateFromUnknown(p.date) || new Date(),
              method: p.method || 'stripe',
              amount: typeof p.amount === 'number' ? p.amount : 0,
              comment: p.comment || '',
              paymentMethodType: p.paymentMethodType || 'unknown',
              paymentMethodDetails: p.paymentMethodDetails || null,
              chargeId: p.chargeId || null,
              receiptUrl: p.receiptUrl || null
            })) : []
          });
        };

        const registrationSnapshot = await getDocs(regQuery);
        registrationSnapshot.forEach(pushRegistrationDoc);

      }
    })
  );

  results.sort((a, b) => {
    const aTime = a.startTime?.getTime?.() ?? 0;
    const bTime = b.startTime?.getTime?.() ?? 0;
    return aTime - bTime;
  });

  return results;
}

async function determineCheckpointAvailability(eventEditionId: string, userId: string): Promise<boolean> {
  const personId = ensureNumber(userId);
  if (personId == null) {
    return false;
  }
  const checkpointsRef = collection(db, 'checkpointResults');
  const q = query(
    checkpointsRef,
    where('eventEditionId', '==', eventEditionId),
    where('personId', '==', personId),
    limit(1)
  );
  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

async function getRaceResult(
  editionId: string,
  personId: number,
  races: any[]
): Promise<{ result: any | null; distanceKey?: string | null }> {
  for (let index = 0; index < races.length; index += 1) {
    const race = races[index];
    const distanceKey = deriveDistanceKey(race, index);
    if (!distanceKey || distanceKey === 'total') {
      continue;
    }
    const raceRef = doc(db, `kutcResults/${editionId}/races/${distanceKey}/results/${personId}`);
    const raceSnap = await getDoc(raceRef);
    if (raceSnap.exists()) {
      return { result: raceSnap.data(), distanceKey };
    }
  }
  return { result: null, distanceKey: null };
}

async function getKUTCParticipations(userId: string, personId: number): Promise<RunnerParticipation[]> {
  const participations: RunnerParticipation[] = [];
  const kutcCollection = collection(db, 'kutcResults');
  const editionsSnapshot = await getDocs(kutcCollection);

  for (const editionDoc of editionsSnapshot.docs) {
    const editionId = editionDoc.id;
    const totalResultRef = doc(db, `kutcResults/${editionId}/races/total/results/${personId}`);
    const totalResultSnap = await getDoc(totalResultRef);
    if (!totalResultSnap.exists()) {
      continue;
    }

    const totalResult = totalResultSnap.data() as any;
    const loopsCompleted = ensureLoops(totalResult.loopsCompleted);
    const totalRank = ensureNumber(totalResult.finalRank);
    const totalTimeSeconds = ensureNumber(totalResult.totalTimeSeconds);
    const totalTimeDisplay = totalResult.totalTimeDisplay || totalResult.totalTime || null;
    const status = totalResult.status || null;

    const rawData = editionDoc.data() as any;
    const metadata = rawData?.metadata || rawData?.summary || {};
    const races = Array.isArray(metadata?.races) ? metadata.races : [];
    const eventDate = toDateSafe(metadata?.eventDate);
    const year = ensureNumber(metadata?.year) ?? parseYearFromEditionId(editionId);

    let raceName = totalResult.raceName || totalResult.race || '';
    let raceRank = ensureNumber(totalResult.raceRank);
    let raceTimeSeconds = ensureNumber(totalResult.raceTimeSeconds);
    let raceTimeDisplay = totalResult.raceTimeDisplay || null;
    let distanceKey: string | null | undefined = totalResult.distanceKey || null;

    if (!raceName || raceRank === null || raceTimeSeconds === null) {
      const { result: raceResult, distanceKey: detectedKey } = await getRaceResult(editionId, personId, races);
      if (raceResult) {
        distanceKey = detectedKey;
        raceName = raceResult.raceName || raceResult.distanceName || raceName;
        raceRank = ensureNumber(raceResult.raceRank ?? raceResult.race_rank);
        raceTimeSeconds = ensureNumber(raceResult.raceTimeSeconds ?? raceResult.race_time_seconds);
        raceTimeDisplay = raceResult.raceTimeDisplay || raceResult.race_time_display || raceTimeDisplay;
      }
    }

    if (!raceName) {
      const raceIndex = races.findIndex((race: any) => {
        const key = deriveDistanceKey(race, 0);
        return key === distanceKey;
      });
      if (raceIndex >= 0) {
        raceName = races[raceIndex]?.raceName || races[raceIndex]?.name || 'Race';
      }
    }

    const hasCheckpointData = await determineCheckpointAvailability(editionId, String(personId));

    participations.push({
      editionId,
      year: year ?? parseYearFromEditionId(editionId),
      eventDate,
      raceName: raceName || 'Race',
      distanceKey,
      raceRank,
      raceTimeSeconds,
      raceTimeDisplay,
      totalRank,
      totalTimeSeconds,
      totalTimeDisplay,
      loopsCompleted,
      status,
      hasCheckpointData
    });
  }

  participations.sort((a, b) => b.year - a.year);
  return participations;
}

export async function getRunnerKutcParticipationData(userId: string, personId: number): Promise<RunnerKutcParticipationData> {
  const participations = await getKUTCParticipations(userId, personId);
  const countedParticipations = participations.filter((participation) =>
    participation.status?.toUpperCase() !== 'DNS'
  );

  const totalAppearances = countedParticipations.length;
  const totalLoops = participations.reduce((sum, item) => sum + item.loopsCompleted, 0);
  const appearanceYears = Array.from(new Set(countedParticipations.map((item) => item.year)))
    .filter((year): year is number => Number.isFinite(year) && year > 0)
    .sort((a, b) => a - b);

  let bestPerformance: RunnerBestPerformance | null = null;
  for (const participation of participations) {
    if (participation.loopsCompleted <= 0) {
      continue;
    }
    if (!bestPerformance) {
      bestPerformance = {
        editionId: participation.editionId,
        year: participation.year,
        loops: participation.loopsCompleted,
        totalTimeSeconds: participation.totalTimeSeconds ?? null,
        totalTimeDisplay: participation.totalTimeDisplay,
        raceName: participation.raceName
      };
      continue;
    }

    if (participation.loopsCompleted > bestPerformance.loops) {
      bestPerformance = {
        editionId: participation.editionId,
        year: participation.year,
        loops: participation.loopsCompleted,
        totalTimeSeconds: participation.totalTimeSeconds ?? null,
        totalTimeDisplay: participation.totalTimeDisplay,
        raceName: participation.raceName
      };
      continue;
    }

    if (participation.loopsCompleted === bestPerformance.loops) {
      const currentTime = participation.totalTimeSeconds ?? Number.POSITIVE_INFINITY;
      const bestTime = bestPerformance.totalTimeSeconds ?? Number.POSITIVE_INFINITY;
      if (currentTime < bestTime) {
        bestPerformance = {
          editionId: participation.editionId,
          year: participation.year,
          loops: participation.loopsCompleted,
          totalTimeSeconds: participation.totalTimeSeconds ?? null,
          totalTimeDisplay: participation.totalTimeDisplay,
          raceName: participation.raceName
        };
      }
    }
  }

  return {
    participations,
    totalAppearances,
    appearanceYears,
    totalLoops,
    bestPerformance
  };
}

export async function getRunnerProfile(userId: string, options?: GetRunnerProfileOptions): Promise<RunnerProfile> {
  const includePrivateDetails = options?.includePrivateDetails === true;
  const numericPersonId = ensureNumber(userId);

  if (!includePrivateDetails && numericPersonId != null) {
    const publicSnap = await getDoc(doc(db, 'publicProfiles', String(numericPersonId)));
    if (!publicSnap.exists()) {
      throw new Error('Runner not found');
    }

    const publicData = publicSnap.data() as FirestorePublicProfile;
    const personId = ensureNumber(publicData.personId ?? numericPersonId) ?? numericPersonId;
    const firstName = publicData.firstName || publicData.displayName?.split(' ')[0] || '';
    const lastName = publicData.lastName || publicData.displayName?.split(' ').slice(1).join(' ') || '';
    const routeId = String(personId);

    const includeParticipations = options?.includeParticipations !== false;
    const includeUpcomingRegistrations = options?.includeUpcomingRegistrations === true;

    let participations: RunnerParticipation[] = [];
    let totalAppearances = 0;
    let totalLoops = 0;
    let appearanceYears: number[] = [];
    let bestPerformance: RunnerBestPerformance | null = null;

    if (includeParticipations) {
      const data = await getRunnerKutcParticipationData(routeId, personId);
      participations = data.participations;
      totalAppearances = data.totalAppearances;
      totalLoops = data.totalLoops;
      appearanceYears = data.appearanceYears;
      bestPerformance = data.bestPerformance;
    }

    const upcomingRegistrations = includeUpcomingRegistrations
      ? await getPublicUpcomingRegistrations(personId)
      : [];

    return {
      routeId,
      userId: routeId,
      userDocId: routeId,
      personId,
      firstName,
      lastName,
      email: '',
      phoneCountryCode: null,
      phone: null,
      totalAppearances,
      appearanceYears,
      totalLoops,
      bestPerformance,
      participations,
      upcomingRegistrations
    };
  }

  let userSnap;
  if (options?.identityMode === 'auth-account') {
    userSnap = await resolveAuthUserDocument(db, userId);
  } else {
    // Explicit historical document routes remain distinct from Auth UID intent.
    userSnap = await getDoc(doc(db, 'users', userId));
    if (!userSnap.exists()) {
      const usersRef = collection(db, 'users');
      const candidates = new Map<string, typeof userSnap>();
      for (const [field, value] of [['uid', userId], ['userId', userId],
        ...(Number.isSafeInteger(Number(userId)) && Number(userId) > 0 ? [['personId', Number(userId)]] : [])] as [string, string | number][]) {
        const found = await getDocs(query(usersRef, where(field, '==', value), limit(2)));
        if (found.size > 1) throw new Error('Ambiguous runner identity requires review.');
        found.docs.forEach(candidate => candidates.set(candidate.id, candidate));
      }
      if (candidates.size > 1) throw new Error('Ambiguous runner identity requires review.');
      userSnap = Array.from(candidates.values())[0] || userSnap;
    }
  }

  if (!userSnap?.exists()) {
    throw new Error('Runner not found');
  }

  const userData = userSnap.data() as FirestoreUser;
  const userDocId = userSnap.id;

  const normalizeSafeCandidate = (value: string | null | undefined): string | null => {
    const normalized = normalizeId(value ?? undefined);
    if (!normalized) {
      return null;
    }
    return normalized.includes('@') ? null : normalized;
  };

  // Registration foreign keys name the exact users document, not its Auth UID.
  const effectiveUserId = userDocId;

  const firstName = userData.firstName || userData.displayName?.split(' ')[0] || '';
  const lastName = userData.lastName || userData.displayName?.split(' ').slice(1).join(' ') || '';
  const personId = ensureNumber(userData.personId);

  const routeId = personId !== null && Number.isFinite(personId)
    ? String(personId)
    : (
        normalizeSafeCandidate(userData.uid ?? null) ??
        normalizeSafeCandidate(userData.userId ?? null) ??
        normalizeSafeCandidate(userDocId) ??
        normalizeSafeCandidate(userId) ??
        userId
      );

  const includeParticipations = options?.includeParticipations !== false;
  const includeUpcomingRegistrations = options?.includeUpcomingRegistrations === true;

  let participations: RunnerParticipation[] = [];
  let totalAppearances = 0;
  let totalLoops = 0;
  let appearanceYears: number[] = [];
  let bestPerformance: RunnerBestPerformance | null = null;

  if (includeParticipations && personId !== null) {
    const data = await getRunnerKutcParticipationData(effectiveUserId, personId);
    participations = data.participations;
    totalAppearances = data.totalAppearances;
    totalLoops = data.totalLoops;
    appearanceYears = data.appearanceYears;
    bestPerformance = data.bestPerformance;
  }

  const upcomingRegistrations = includeUpcomingRegistrations
    ? (
        includePrivateDetails
          ? await fetchUpcomingRegistrations(effectiveUserId, personId)
          : (personId !== null ? await getPublicUpcomingRegistrations(personId) : [])
      )
    : [];

  return {
    routeId,
    userId: effectiveUserId,
    userDocId,
    personId,
    firstName,
    lastName,
    email: includePrivateDetails ? (userData.email || '') : '',
    phoneCountryCode: includePrivateDetails ? (userData.phoneCountryCode || null) : null,
    phone: includePrivateDetails ? (userData.phone || null) : null,
    displayName: includePrivateDetails ? (userData.displayName || undefined) : undefined,
    dateOfBirth: includePrivateDetails ? (userData.dateOfBirth || null) : null,
    yearOfBirth: includePrivateDetails ? (userData.yearOfBirth || null) : null,
    nfifLicenseNumber: includePrivateDetails ? (userData.nfifLicenseNumber || null) : null,
    gender: includePrivateDetails ? (userData.gender || '') : '',
    nationality: includePrivateDetails ? (userData.nationality || null) : null,
    representing: includePrivateDetails ? (Array.isArray(userData.representing) ? userData.representing : []) : [],
    sendRunningOffers: includePrivateDetails ? (userData.sendRunningOffers || false) : false,
    notifyFutureEvents: includePrivateDetails ? (userData.notifyFutureEvents || false) : false,
    languagePreference: includePrivateDetails ? (
      (() => {
        if (userData.languagePreference) {
          return userData.languagePreference;
        } else {
          const appLanguage = typeof window !== 'undefined' && window.localStorage 
            ? window.localStorage.getItem(LOCALE_STORAGE_KEY) || 'en'
            : 'en';
          
          // Map app language codes to our preference codes
          const detectedLang = appLanguage === 'no' ? 'no' : 'en';
          return detectedLang;
        }
      })()
    ) : null,
    totalAppearances,
    appearanceYears,
    totalLoops,
    bestPerformance,
    participations,
    upcomingRegistrations
  };
}

export async function ensureLanguagePreference(userId: string): Promise<void> {
  if (!userId) {
    throw new Error('Missing userId for language preference check');
  }

  const strictIdentity = process.env.REACT_APP_PERSON_IDENTITY_POLICY === 'strict-v1';
  const userDoc = strictIdentity
    ? await resolveAuthUserDocument(db, userId)
    : await getDoc(doc(db, 'users', userId));
  if (!userDoc || !userDoc.exists()) {
    return;
  }

  const userData = userDoc.data() as FirestoreUser;
  
  // If languagePreference is not set, set a default based on app language preference
  if (!userData.languagePreference) {
    const appLanguage = typeof window !== 'undefined' && window.localStorage 
      ? window.localStorage.getItem(LOCALE_STORAGE_KEY) || 'en'
      : 'en';
    
    // Map app language codes to our preference codes
    const defaultLanguage = appLanguage === 'no' ? 'no' : 'en';
    
    await setDoc(userDoc.ref, strictIdentity
      ? { languagePreference: defaultLanguage }
      : { languagePreference: defaultLanguage, uid: userId }, { merge: true });
  }
}

export async function updateRunnerProfileDetails(
  userId: string,
  details: RunnerProfileEditableDetails,
  expectedIdentity?: LoadedRunnerProfileIdentity
): Promise<void | RunnerProfileUpdateResult> {
  if (!userId) {
    throw new Error('Missing userId for runner profile update');
  }

  if (process.env.REACT_APP_PERSON_IDENTITY_POLICY === 'strict-v1') {
    if (!expectedIdentity || expectedIdentity.userDocId !== userId) {
      throw new Error('Loaded runner identity is required for profile updates.');
    }
    return submitStrictRunnerProfileUpdate('admin', userId, details, expectedIdentity);
  }

  // Read existing doc first to detect per-consent field changes
  const existingDoc = await getDoc(doc(db, 'users', userId));
  const existing = existingDoc.exists() ? existingDoc.data() : {};

  // Handle dateOfBirth/yearOfBirth merge logic
  let dateOfBirth = details.dateOfBirth;
  let yearOfBirth = details.yearOfBirth;
  if (yearOfBirth !== null && yearOfBirth !== undefined && typeof yearOfBirth !== 'number') {
    const parsed = Number(yearOfBirth);
    yearOfBirth = Number.isFinite(parsed) ? parsed : null;
  }
  
  // If dateOfBirth is provided but yearOfBirth is not, derive year from date
  if (dateOfBirth && !yearOfBirth) {
    yearOfBirth = dateOfBirth.getFullYear();
  }
  
  // If yearOfBirth is provided but dateOfBirth is not, keep yearOfBirth as is
  // (user can fill in missing data later)

  const newNotify = details.notifyFutureEvents || false;
  const newSendOffers = details.sendRunningOffers || false;

  const payload: Record<string, any> = {
    firstName: details.firstName,
    lastName: details.lastName,
    displayName: details.displayName || `${details.firstName} ${details.lastName}`,
    phoneCountryCode: details.phoneCountryCode ?? '',
    phone: details.phone ?? '',
    dateOfBirth: dateOfBirth,
    yearOfBirth: yearOfBirth,
    nfifLicenseNumber: details.nfifLicenseNumber || null,
    gender: details.gender ? details.gender.toLowerCase() : '',
    nationality: details.nationality || null,
    representing: details.representing || [],
    sendRunningOffers: newSendOffers,
    notifyFutureEvents: newNotify,
    languagePreference: details.languagePreference || null
  };

  // Write per-consent tracking fields for any consent that changed
  if (existing.notifyFutureEvents !== newNotify) {
    payload.notifyFutureEvents_consent_updated_at = serverTimestamp();
    payload.notifyFutureEvents_consent_source = 'my_page';
  }
  if (existing.sendRunningOffers !== newSendOffers) {
    payload.sendRunningOffers_consent_updated_at = serverTimestamp();
    payload.sendRunningOffers_consent_source = 'my_page';
  }

  // Only remove undefined values, preserve null and false values
  Object.keys(payload).forEach(key => {
    if (payload[key] === undefined) {
      delete payload[key];
    }
  });

  await setDoc(doc(db, 'users', userId), payload, { merge: true });
  
  // Also update publicProfiles if personId exists
  // Re-use existing read if personId was already present; otherwise re-read
  const personId = existing.personId || (existingDoc.exists() ? existingDoc.data()?.personId : undefined);
  const userDoc = personId ? existingDoc : await getDoc(doc(db, 'users', userId));
  if (userDoc.exists() && userDoc.data().personId) {
    const publicPayload: Record<string, any> = {
      firstName: details.firstName,
      lastName: details.lastName,
      displayName: details.displayName || `${details.firstName} ${details.lastName}`,
      nationality: details.nationality || null,
      dateOfBirth: dateOfBirth,
      yearOfBirth: yearOfBirth,
      gender: details.gender ? details.gender.toLowerCase() : '',
      representing: details.representing || [],
      updatedAt: new Date()
    };
    
    // Remove undefined/null values
    Object.keys(publicPayload).forEach(key => {
      if (publicPayload[key] === undefined || publicPayload[key] === null) {
        delete publicPayload[key];
      }
    });
    
    await setDoc(doc(db, 'publicProfiles', String(userDoc.data().personId)), publicPayload, { merge: true });
  }
}

/** Update the authenticated runner's reviewed profile, resolving its canonical
 * users document from the authenticated UID. Keep updateRunnerProfileDetails for
 * explicit document-ID callers such as admin profile editing.
 */
export async function updateOwnRunnerProfileDetails(
  authUid: string,
  details: RunnerProfileEditableDetails,
  expectedIdentity?: LoadedRunnerProfileIdentity
): Promise<void | RunnerProfileUpdateResult> {
  if (process.env.REACT_APP_PERSON_IDENTITY_POLICY === 'strict-v1') {
    if (!expectedIdentity || !expectedIdentity.userDocId || !authUid) {
      throw new Error('Loaded runner identity is required for profile updates.');
    }
    return submitStrictRunnerProfileUpdate('own', expectedIdentity.userDocId, details, expectedIdentity);
  }
  const userDoc = await resolveAuthUserDocument(db, authUid);
  if (!userDoc) throw new Error('No runner profile is linked to this account.');
  await updateRunnerProfileDetails(userDoc.id, details);
}

const strictProfileDetailKeys = new Set([
  'firstName', 'lastName', 'displayName', 'phoneCountryCode', 'phone', 'dateOfBirth', 'yearOfBirth',
  'nfifLicenseNumber', 'gender', 'nationality', 'representing', 'sendRunningOffers', 'notifyFutureEvents',
  'languagePreference',
]);

function strictProfileDetails(details: RunnerProfileEditableDetails): Record<string, unknown> {
  if (!details || typeof details !== 'object' || Array.isArray(details)
    || Reflect.ownKeys(details).some(key => typeof key !== 'string' || !strictProfileDetailKeys.has(key))) {
    throw new Error('Invalid profile fields.');
  }
  const result: Record<string, unknown> = {};
  const boundedString = (value: unknown, max: number, required = false): string => {
    if (typeof value !== 'string') throw new Error('Invalid profile fields.');
    const trimmed = value.trim();
    if (trimmed.length > max || (required && !trimmed)) throw new Error('Invalid profile fields.');
    return trimmed;
  };
  result.firstName = boundedString(details.firstName, 128, true);
  result.lastName = boundedString(details.lastName, 128, true);
  const strings: Array<[keyof RunnerProfileEditableDetails, number]> = [
    ['displayName', 256], ['phoneCountryCode', 16], ['phone', 32], ['nfifLicenseNumber', 64], ['nationality', 64],
  ];
  for (const [key, max] of strings) {
    const value = details[key];
    if (value !== undefined) {
      if ((key === 'nfifLicenseNumber' || key === 'nationality') && value === null) result[key] = null;
      else result[key] = boundedString(value, max);
    }
  }
  if (details.dateOfBirth !== undefined) {
    if (details.dateOfBirth === null) result.dateOfBirth = null;
    else if (details.dateOfBirth instanceof Date && Number.isFinite(details.dateOfBirth.getTime()))
      result.dateOfBirth = details.dateOfBirth.toISOString();
    else throw new Error('Invalid profile fields.');
  }
  let yearOfBirth = details.yearOfBirth;
  if (yearOfBirth === undefined && details.dateOfBirth instanceof Date && Number.isFinite(details.dateOfBirth.getTime()))
    yearOfBirth = details.dateOfBirth.getFullYear();
  if (yearOfBirth !== undefined) {
    if (yearOfBirth !== null && (!Number.isInteger(yearOfBirth) || yearOfBirth < 1850
      || yearOfBirth > new Date().getFullYear() + 1)) throw new Error('Invalid profile fields.');
    result.yearOfBirth = yearOfBirth;
  }
  if (details.gender !== undefined) {
    if (details.gender !== 'male' && details.gender !== 'female' && details.gender !== '')
      throw new Error('Invalid profile fields.');
    result.gender = details.gender;
  }
  if (details.representing !== undefined) {
    if (!Array.isArray(details.representing) || details.representing.length > 100)
      throw new Error('Invalid profile fields.');
    result.representing = details.representing.map(value => boundedString(value, 128, true));
  }
  for (const key of ['sendRunningOffers', 'notifyFutureEvents'] as const) {
    if (details[key] !== undefined) {
      if (typeof details[key] !== 'boolean') throw new Error('Invalid profile fields.');
      result[key] = details[key];
    }
  }
  if (details.languagePreference !== undefined) {
    if (details.languagePreference !== null && details.languagePreference !== 'en' && details.languagePreference !== 'no')
      throw new Error('Invalid profile fields.');
    result.languagePreference = details.languagePreference;
  }
  return result;
}

async function submitStrictRunnerProfileUpdate(
  mode: 'own' | 'admin',
  expectedUserId: string,
  details: RunnerProfileEditableDetails,
  expectedIdentity: LoadedRunnerProfileIdentity
): Promise<RunnerProfileUpdateResult> {
  const { userDocId, personId } = expectedIdentity;
  if (!userDocId || userDocId !== expectedUserId || userDocId.includes('/')
    || (personId !== null && (!Number.isSafeInteger(personId) || personId <= 0))) {
    throw new Error('Loaded runner identity is invalid.');
  }
  const payload = {
    mode, expectedUserId, expectedPersonId: personId, details: strictProfileDetails(details),
  };
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const callable = httpsCallable<typeof payload, RunnerProfileUpdateResult>(getFunctions(undefined, 'europe-west1'), 'updateRunnerProfileV2');
  const response = (await callable(payload)).data;
  if (!response || response.status !== 'saved'
    || !['published', 'held', 'unassigned'].includes(response.publicPublication)) {
    throw new Error('The profile update returned an invalid result.');
  }
  return response;
}
